/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateX,
  useStateXAtomSetter,
  useStateXSelector,
} from '@cloudio-saas/statex';
import {
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ExpressionOnlyProp, ExpressionOnlyValue } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import { expressionPropsAtom } from 'expression/builder/state';
import useLocalState from 'hooks/useLocalState';
import Icon from 'icons/Icon';
import { PlatformContext } from 'platform/PlatformProvider';
import React, {
  memo,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
});

function ExpressionProperty({
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  onChange,
  appUid,
  value: text = getDefaultValues<ExpressionOnlyValue>(mgr, property),
  isViewOnlyUser = false,
}: PropertyEditorProps<ExpressionOnlyProp, ExpressionOnlyValue>) {
  const platform = useContext(PlatformContext);
  const [, setState] = useState([]);
  const classes = useStyles();
  const [undoable, setUndoable] = useState(true);
  const [localValue, setLocalValue] = useLocalState(text, (val) => {
    onChange(property, val, undoable);
    setExpressionValue(val);
    setUndoable(false);
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const setExpressionProps = useStateXAtomSetter(expressionPropsAtom);

  // do not use the designer path, instead use some transient path
  // as this could be used in UpdateProp Action in which case we need to
  // call onChange to change the property and not update the designer path directly
  const path = ['transient', '_expression_', activeItemId, property];
  const [expressionValue, setExpressionValue] = useStateX<
    ExpressionOnlyValue | undefined
  >(path, undefined);

  useEffect(() => {
    // reset the value in the trasient path
    setExpressionValue(undefined);
  }, [setExpressionValue]);

  useEffect(() => {
    if (expressionValue !== undefined) {
      // user have selected some item in the popup
      setLocalValue(expressionValue);
      inputRef.current?.focus();
    }
  }, [expressionValue, onChange, property, setLocalValue]);

  useEffect(() => {
    if (def.refreshCallback) {
      def.refreshCallback(() => {
        setState([]);
      });
    }
  }, [def]);
  const id = useId();

  const page = platform.getPage(appUid, pageId);
  const itemWithDefaults = itemWithDefaultValues(page[activeItemId]);
  const returnType = def.returnType(itemWithDefaults, {
    appUid,
    itemId: activeItemId,
    pageId,
    mgr,
    platform,
  });
  const openExpressionBuilder = (val: string) => {
    setExpressionProps({
      path,
      returnType,
      type: 'string',
      open: true,
      value: { type: 'expression', value: val },
      context: {
        appUid,
        pageId,
        itemId: activeItemId,
        property,
        data: {},
      },
    });
  };

  return (
    <>
      <FormControl
        disabled={isViewOnlyUser}
        data-testid={property}
        className={classes.root}
        classes={{ root: classes.input }}
        fullWidth
        variant="standard"
        required={!!def.required}
      >
        <InputLabel htmlFor={id}>{def.label}</InputLabel>
        <Input
          disabled={isViewOnlyUser}
          id={id}
          spellCheck={false}
          autoCorrect="off"
          fullWidth
          placeholder={def.placeholder}
          inputRef={inputRef}
          onFocus={(e) => {
            setUndoable(true);
            e.target.select();
          }}
          onBlur={() => {
            setUndoable(true);
          }}
          type="text"
          value={localValue ?? ''}
          onChange={(e) => {
            setLocalValue(e.target.value);
            setExpressionValue(undefined);
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                disabled={isViewOnlyUser}
                onClick={() => {
                  setExpressionValue(undefined);
                  openExpressionBuilder(localValue);
                }}
                aria-label="Open Expression Builder"
              >
                <span aria-hidden="false" style={{ display: 'none' }}>
                  Open Expression Builder
                </span>
                <Icon disabled={isViewOnlyUser} iconName="sigma" />
              </IconButton>
              {helpText ? (
                <Icon
                  data-tip-at="top"
                  data-tip-delay={0}
                  data-tip={helpText}
                  iconName="comment-exclamation"
                />
              ) : null}
            </InputAdornment>
          }
        />
      </FormControl>
    </>
  );
}

export default memo(ExpressionProperty);
